import { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import FileDrop from '../File/FileDrop'
import TicketTypePicker from './TicketTypePicker'
import OriginPicker from './OriginPicker'
import ImpactUrgencyPicker from './ImpactUrgencyPicker'
import TicketReview from './TicketReview'
import useTicketDraft from '../../hooks/useTicketDraft'
import useCreateTicket from '../../hooks/useCreateTicket'
import useTicketPriorities from '../../hooks/useTicketPriorities'
import { composeDescription } from '../../utils/ticketDraft'
import { getTicketType } from '../../utils/ticketType'
import { DETAIL_QUESTIONS, WIZARD_STEPS } from '../../utils/ticketCreateFlows'
import '../../styles/ticket-form.css'

/**
 * Modo "paso a paso": una pregunta por pantalla hasta el resumen final.
 * Los pasos y qué campos valida cada uno viven en `WIZARD_STEPS`.
 *
 * @component
 * @returns {JSX.Element}
 */
const TicketWizard = () => {
  const { draft, errors, setField, patch, validate } = useTicketDraft()
  const { submit, saving, uploading } = useCreateTicket()
  const { priorityLabel } = useTicketPriorities()
  const [index, setIndex] = useState(0)
  const [direction, setDirection] = useState(1)
  const [answers, setAnswers] = useState({})
  const [files, setFiles] = useState([])

  const step = WIZARD_STEPS[index]
  const isLast = index === WIZARD_STEPS.length - 1
  const isBlocked = saving || uploading

  /** Las respuestas guiadas se unen en la descripción del ticket. */
  const setAnswer = (key, value) => {
    const next = { ...answers, [key]: value }
    setAnswers(next)
    setField(
      'description',
      composeDescription(
        DETAIL_QUESTIONS.map((q) => ({ label: q.label, value: next[q.key] }))
      )
    )
  }

  const goTo = (target) => {
    setDirection(target > index ? 1 : -1)
    setIndex(target)
  }

  const next = async () => {
    if (!validate(step.fields)) return
    if (isLast) await submit(draft, files)
    else goTo(index + 1)
  }

  const typeMeta = getTicketType(draft.type)

  /** Contenido de cada paso, por clave de `WIZARD_STEPS`. */
  const STEP_CONTENT = {
    type: () => (
      <TicketTypePicker
        value={draft.type}
        onChange={(value) => setField('type', value)}
        showExamples
      />
    ),
    origin: () => (
      <OriginPicker
        variant="cards"
        value={draft.systemOrigin}
        onChange={(value) => setField('systemOrigin', value)}
      />
    ),
    title: () => (
      <>
        <input
          type="text"
          className={`form-control form-control-lg at-input ${errors.title ? 'is-invalid' : ''}`}
          placeholder={`Ej: ${typeMeta.examples[0]}`}
          value={draft.title}
          maxLength={150}
          autoFocus
          onChange={(e) => setField('title', e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && next()}
        />
        <p className="at-level-hint">
          Sea específico: mencione el sistema y el síntoma.
        </p>
      </>
    ),
    detail: () => (
      <div className="d-flex flex-column gap-3">
        {DETAIL_QUESTIONS.map((q) => (
          <div key={q.key}>
            <label className="at-label" htmlFor={`wz-${q.key}`}>
              {q.label} {q.required && <span className="at-req">*</span>}
            </label>
            <textarea
              id={`wz-${q.key}`}
              className="form-control at-input"
              rows={q.required ? 3 : 2}
              placeholder={q.placeholder}
              value={answers[q.key] || ''}
              onChange={(e) => setAnswer(q.key, e.target.value)}
            />
          </div>
        ))}
      </div>
    ),
    priority: () => (
      <ImpactUrgencyPicker
        impact={draft.impact}
        urgency={draft.urgency}
        priorityId={draft.priorityId}
        priorityLabel={priorityLabel}
        onChange={patch}
      />
    ),
    files: () => <FileDrop files={files} setFiles={setFiles} />,
    review: () => (
      <TicketReview
        draft={draft}
        onChange={patch}
        errors={errors}
        priorityLabel={priorityLabel}
        files={files}
      />
    ),
  }

  // Errores del paso que no tienen un campo propio donde mostrarse.
  const stepErrors =
    step.key === 'review'
      ? []
      : step.fields.map((field) => errors[field]).filter(Boolean)

  return (
    <div className="wz">
      {/* PROGRESO */}
      <div className="wz-progress" aria-label="Progreso">
        {WIZARD_STEPS.map((s, i) => (
          <button
            key={s.key}
            type="button"
            className={`wz-dot ${i === index ? 'is-current' : ''} ${i < index ? 'is-done' : ''}`}
            onClick={() => i < index && goTo(i)}
            disabled={i >= index}
            title={s.title}
            aria-current={i === index ? 'step' : undefined}
          >
            <i className={`fa-solid ${i < index ? 'fa-check' : s.icon}`} />
          </button>
        ))}
        <div className="wz-bar">
          <span
            style={{ width: `${(index / (WIZARD_STEPS.length - 1)) * 100}%` }}
          />
        </div>
      </div>

      <div className="at-card wz-card">
        <AnimatePresence mode="wait" initial={false} custom={direction}>
          <motion.div
            key={step.key}
            custom={direction}
            initial={{ opacity: 0, x: 24 * direction }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 * direction }}
            transition={{ duration: 0.18 }}
          >
            <span className="wz-count">
              Paso {index + 1} de {WIZARD_STEPS.length}
            </span>
            <h5 className="wz-title">{step.title}</h5>
            <p className="wz-subtitle">{step.subtitle}</p>

            {STEP_CONTENT[step.key]()}

            {stepErrors.map((msg) => (
              <p key={msg} className="at-error-text">
                <i className="fa-solid fa-circle-exclamation me-1" />
                {msg}
              </p>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="at-actions justify-content-between">
        <button
          type="button"
          className="btn at-btn-ghost"
          onClick={() => goTo(index - 1)}
          disabled={index === 0 || isBlocked}
        >
          <i className="fa-solid fa-arrow-left me-2" />
          Atrás
        </button>
        <button
          type="button"
          className="btn at-btn-primary"
          onClick={next}
          disabled={isBlocked}
        >
          {isBlocked ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              {uploading ? 'Subiendo archivos…' : 'Enviando…'}
            </>
          ) : isLast ? (
            <>
              <i className="fa-solid fa-paper-plane me-2" />
              Enviar ticket
            </>
          ) : (
            <>
              {step.key === 'files' && files.length === 0
                ? 'Omitir'
                : 'Siguiente'}
              <i className="fa-solid fa-arrow-right ms-2" />
            </>
          )}
        </button>
      </div>

      <style>{`
        .wz { display: flex; flex-direction: column; gap: 16px; }
        .wz-progress { position: relative; display: flex; justify-content: space-between; padding: 0 4px; }
        .wz-bar { position: absolute; left: 20px; right: 20px; top: 50%; height: 3px; background: var(--gm-border); z-index: 0; border-radius: 99px; }
        .wz-bar span { display: block; height: 100%; background: var(--gm-primary); border-radius: 99px; transition: width 0.3s ease; }
        .wz-dot {
          position: relative; z-index: 1; width: 34px; height: 34px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center; font-size: 0.75rem;
          border: 2px solid var(--gm-border); background: var(--gm-surface); color: var(--gm-text-faint);
        }
        .wz-dot.is-done { border-color: var(--gm-primary); background: var(--gm-primary); color: #fff; cursor: pointer; }
        .wz-dot.is-current { border-color: var(--gm-primary); color: var(--gm-primary); box-shadow: 0 0 0 4px var(--gm-primary-soft); }
        .wz-dot:disabled { cursor: default; }
        .wz-card { min-height: 320px; padding: 24px; overflow: hidden; }
        .wz-count { font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--gm-primary); }
        .wz-title { font-weight: 700; margin: 4px 0 2px; color: var(--gm-text); }
        .wz-subtitle { font-size: 0.88rem; color: var(--gm-text-muted); margin-bottom: 18px; }
      `}</style>
    </div>
  )
}

export default TicketWizard
